"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EmployeeContractBuilder = void 0;
const ui_1 = require("@app/component/ui");
const data_grid_1 = require("@app/component/ui/data-grid");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const separator_builder_1 = require("@app/util/builders/separator-builder");
const format_hours_1 = require("@app/util/format-hours");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const useShiftOperations_1 = require("../util/hooks/useShiftOperations");
const employee_contract_form_1 = require("./employee-contract-form");
const getContractTypeLabel = (workspace, contract) => [
    ...(workspace?.contractTypes ?? []),
    {
        id: 'external',
        name: (0, util_1.t)('ORG_SP_SETTINGS_MODULES_EXTERNAL'),
        rules: {
            vacationFund: true,
            fixedWorkHours: false,
        },
    },
]?.find((c) => c.id === contract.type)?.name ?? '';
const EmployeeContractBuilder = ({ contracts, userPositions, setEditing, editing, fields, control, isReadOnly, }) => {
    const workspace = (0, store_1.useRootSelector)((state) => state.workspaces.find((ws) => ws.id === state.workspaceId));
    const isEditingCurrentContract = react_1.default.useCallback((contractId) => {
        if ((0, util_1.isUndefined)(contractId))
            return true;
        return editing.includes(contractId);
    }, [editing]);
    const SHIFT_OPERATIONS = (0, useShiftOperations_1.useShiftOperations)();
    if (contracts.length === 0) {
        return (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8, type: ui_1.Spacing.TYPES.VERTICAL }),
            react_1.default.createElement(ui_1.Text, { type: ui_1.Text.TYPES.BODY_LARGE }, (0, util_1.t)('NONE'))));
    }
    return (react_1.default.createElement(separator_builder_1.SeparatorBuilder, { elements: contracts, separator: react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_16, type: ui_1.Spacing.TYPES.VERTICAL }), getKey: (el, index) => `contract-${el.id ?? index}` }, (contract) => (react_1.default.createElement(react_1.default.Fragment, null,
        isEditingCurrentContract((0, maybe_unwrap_1.unwrapMaybe)(contract.id)) ||
            isReadOnly ? null : (react_1.default.createElement("div", { style: { display: 'flex', justifyContent: 'end' } },
            react_1.default.createElement(ui_1.Button, { ico: 'edit', style: 'BORDERLESS', onClick: () => setEditing(contract.id ?? '') }, (0, util_1.t)('EDIT')))),
        react_1.default.createElement(ui_1.Spacing, { size: ui_1.Spacing.SIZES.SIZE_8, type: ui_1.Spacing.TYPES.VERTICAL }),
        isEditingCurrentContract((0, maybe_unwrap_1.unwrapMaybe)(contract.id)) ? (react_1.default.createElement(employee_contract_form_1.EmployeeContractForm, { control: control, index: fields.findIndex((f) => f.cId === contract.id), positions: userPositions ?? [] })) : (react_1.default.createElement(data_grid_1.DataGrid, { header: {
                label: (0, util_1.t)('ATTEN_TYPE'),
                value: getContractTypeLabel(workspace, contract),
            }, columns: [
                {
                    cells: [
                        {
                            label: (0, util_1.t)('BATCH_EMPS_CONTRACT_HOURS_MONTH'),
                            value: (0, format_hours_1.formatHours)(contract.options?.minutesOfWorkPerMonth / 60),
                        },
                        {
                            label: (0, util_1.t)('BATCH_EMPS_CONTRACT_START'),
                            value: (0, util_1.isNull)(contract.period?.start)
                                ? (0, util_1.t)('NOT_SET')
                                : (0, moment_1.default)(contract.period?.start).format('DD.MM.YYYY'),
                        },
                        {
                            label: (0, util_1.t)('POSITION'),
                            value: (react_1.default.createElement("div", { style: {
                                    display: 'flex',
                                    gap: '0.25rem',
                                    width: '100%',
                                    height: '100%',
                                    alignItems: 'center',
                                } }, userPositions
                                ?.filter((up) => contract.positions?.some((p) => p.positionId === up.id))
                                .map((p) => (react_1.default.createElement(ui_1.Position, { key: p.id, pos: p, size: 'small' }))))),
                        },
                        {
                            label: (0, util_1.t)('CONTRACT_INDEX'),
                            value: contract?.contractId,
                        },
                    ],
                },
                {
                    cells: [
                        {
                            label: (0, util_1.t)('BATCH_EMPS_CONTRACT_HOURS_SHIFT'),
                            value: (0, format_hours_1.formatHours)(contract.options?.minutesOfWorkPerShift / 60),
                        },
                        {
                            label: (0, util_1.t)('BATCH_EMPS_CONTRACT_END'),
                            value: (0, util_1.isNull)(contract.period?.end)
                                ? (0, util_1.t)('EMPLOYEE_PROFILE_CONT_DATE_UNDEFINED')
                                : (0, moment_1.default)(contract.period?.end).format('DD.MM.YYYY'),
                        },
                        {
                            label: (0, util_1.t)('LOCALITY'),
                            value: (react_1.default.createElement("div", { style: {
                                    display: 'flex',
                                    flexShrink: 0,
                                    flexWrap: 'wrap',
                                    flexDirection: 'row',
                                } }, fields
                                .find((f) => f.cId === contract.id)
                                ?.localities.map((l, index, array) => {
                                return (react_1.default.createElement("span", { key: l.id, style: { display: 'flex' } },
                                    react_1.default.createElement(ui_1.Locality, { key: l.id, ...l }),
                                    index < array.length ? react_1.default.createElement("div", null, ",\u00A0") : ''));
                            }))),
                        },
                        {
                            label: (0, util_1.t)('BATCH_EMPS_CONTRACT_OPERATION'),
                            value: SHIFT_OPERATIONS[contract.options?.operation],
                        },
                    ],
                },
            ] }))))));
};
exports.EmployeeContractBuilder = EmployeeContractBuilder;
